import { makeStyles } from '@material-ui/core';
import clsx from 'clsx';
import { startOfDay } from 'date-fns';
import React from 'react';
import { CheckCircle, Grid, List } from 'react-feather';
import { Link } from 'react-router-dom';
import { usePrefixTranslation } from '../../../i18n';
import { actions, useTypedDispatch, useTypedSelector } from '../../../redux';
import { theme } from '../../../theme/theme';
import { AssetOverview, AssetType } from '../../../types/asset';
import { enumToStatusType, probabilities } from '../../../types/common';
import { computeOverallRiskLikelihoodConsequence } from '../../../types/plan';
import { Badge, Table } from '../../global';
import { FilterBar } from '../../global/FilterBar';

const useAssetListClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        background: theme.colors.neutral[6],
    },
    header: {
        display: 'flex',
        flexDirection: 'column',
        padding: 40,
        background: theme.colors.darkBlue[1],
        '& > span:first-child': {
            color: theme.colors.white,
            ...theme.typography.page.title,
            marginBottom: 0,
        },
        '& > span:last-child': {
            color: theme.colors.neutral[3],
            ...theme.typography.content.label.default,
        },
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        gap: 16,
        padding: '24px 40px',
    },
    count: {
        color: theme.colors.neutral[2],
        ...theme.fonts.h5,
    },
});

export const AssetList = () => {
    const classes = useAssetListClasses();
    const dispatch = useTypedDispatch();
    const { t } = usePrefixTranslation('asset', { asset: 'list.AssetList' });
    const { asset_list, asset_type } = useTypedSelector(state => ({
        asset_list: state.asset_list,
        asset_type: state.asset_type,
    }));
    const [tablePage, setTablePage] = React.useState(0);
    const [numberOfItemsPerTablePage, setNumberOfItemsPerTablePage] = React.useState(10);
    const extraParams = React.useRef<{ [param: string]: string }>({});
    const update = React.useReducer(() => ({}), {})[1];
    const companyName = 'Georgia Pacific Corporation - Brewton, AL';
    const columnsNames = [
        { key: 'name', element: t('table-header-equipment'), size: 'minmax(250px, 1fr)', sortable: true },
        { key: 'asset_type', element: t('table-header-type'), size: '210px', sortable: true },
        { key: 'PSM', element: t('table-header-psm'), size: '60px' },
        { key: 'Overall Risk', element: t('table-header-risk'), size: '160px' },
        { key: 'External Inspection', element: t('table-header-dates-external'), size: '210px' },
        { key: 'Internal Inspection', element: t('table-header-dates-internal'), size: '210px' },
    ];

    React.useEffect(() => {
        dispatch(actions.asset_list.get());
        dispatch(actions.asset_type.get());
    }, []);

    return (
        <div className={classes.root}>
            <div className={classes.header}>
                <span>{t('title')}</span>
                <span>{companyName}</span>
            </div>
            <div className={clsx(classes.content)}>
                <span className={classes.count}>{t('total', { count: asset_list.totalCount ?? 0 })}</span>
                <FilterBar
                    widgets={[
                        {
                            type: 'search',
                            label: t('filter-search'),
                            debounce: 300,
                            autocompleteKey: 'asset-list',
                            onChange: query => {
                                setTablePage(0);
                                dispatch(actions.asset_list.get({ index: 0, query, extraParams: extraParams.current }));
                            },
                        },
                        {
                            type: 'checker',
                            label: t('filter-psm'),
                            checked: extraParams.current['psm'] === 'True',
                            onChange: checked => {
                                delete extraParams.current['psm'];
                                if (checked) extraParams.current['psm'] = 'True';
                                update();
                                dispatch(actions.asset_list.get({ index: 0, extraParams: extraParams.current }));
                                setTablePage(0);
                            },
                        },
                        {
                            type: 'select',
                            label: t('filter-equipment-type'),
                            options: Object.values(asset_type.items),
                            render: ((option: AssetType) => (
                                <span style={{ textTransform: 'capitalize' }}>{option.name}</span>
                            )) as any,
                            search: ((option: AssetType) => option.name) as any,
                            multi: true,
                            all: true,
                            selected: Object.values(asset_type.items).map(type =>
                                (extraParams.current['asset_type'] ?? '').split(',').includes(type.name),
                            ),
                            onSelected: selected => {
                                extraParams.current['asset_type'] = Object.values(asset_type.items)
                                    .filter((_, i) => selected[i])
                                    .map(type => type.name)
                                    .join(',');
                                update();
                                dispatch(actions.asset_list.get({ index: 0, extraParams: extraParams.current }));
                                setTablePage(0);
                            },
                        },
                        {
                            type: 'select',
                            label: t('filter-risk-level'),
                            options: [...probabilities, 'not-defined'],
                            render: ((option: string) => (
                                <Badge type={enumToStatusType[option]}>{option}</Badge>
                            )) as any,
                            multi: true,
                            selected: [...probabilities, 'not-defined'].map(
                                probability =>
                                    (extraParams.current['overall_risk'] ?? '').split(',').includes(probability) ||
                                    (probability === 'not-defined' &&
                                        (extraParams.current['overall_risk'] ?? '').includes('None')),
                            ),
                            onSelected: selected => {
                                extraParams.current['overall_risk'] = [...probabilities, 'not-defined']
                                    .filter((_, i) => selected[i])
                                    .map(value => (value != 'not-defined' ? value : 'None'))
                                    .join(',');
                                update();
                                dispatch(actions.asset_list.get({ index: 0, extraParams: extraParams.current }));
                                setTablePage(0);
                            },
                        },
                        { type: 'clear' },
                        { type: 'buttons', buttons: [{ children: <List />, selected: true }, { children: <Grid /> }] },
                    ]}
                />
                <Table
                    loading={asset_list.fetching ? numberOfItemsPerTablePage : 0}
                    columns={columnsNames}
                    cells={asset_list.assets.map(asset => [
                        <ColumnEquipment asset={asset} />,
                        <ColumnType asset={asset} />,
                        <ColumnPsm asset={asset} />,
                        <ColumnRisk asset={asset} />,
                        <ColumnDates asset={asset} type='external' />,
                        <ColumnDates asset={asset} type='internal' />,
                    ])}
                    fixed={1}
                    rowBuilder={row => props =>
                        (
                            <Link
                                {...(props as any)}
                                style={{ textDecoration: 'none', ...props.style }}
                                to={{ search: `?asset=${asset_list.assets[row].code}` }}
                            />
                        )}
                    onSortColumn={(column, order) =>
                        dispatch(
                            actions.asset_list.get({
                                query: asset_list.query,
                                sort: { key: columnsNames[column].key, order },
                                index: tablePage,
                                extraParams: extraParams.current,
                            }),
                        )
                    }
                    paginator={{
                        page: tablePage,
                        size: numberOfItemsPerTablePage,
                        sizes: [10, 25, 50, 100],
                        total: asset_list.totalCount ?? 0,
                        onPaginationChange: (page, size) => {
                            setNumberOfItemsPerTablePage(size);
                            setTablePage(page);
                            dispatch(
                                actions.asset_list.get({
                                    query: asset_list.query,
                                    sort: asset_list.sort,
                                    index: page,
                                    pageSize: size,
                                    extraParams: extraParams.current,
                                }),
                            );
                        },
                    }}
                />
            </div>
        </div>
    );
};

const useTableColumnsClasses = makeStyles({
    equipment: {
        display: 'flex',
        flexDirection: 'column',
        '& > span:first-child': {
            color: theme.colors.neutral[1],
            ...theme.fonts.labelSemiBold,
        },
        '& > span:last-child': {
            color: theme.colors.neutral[2],
            ...theme.fonts.caption,
        },
    },
    type: {
        color: theme.colors.neutral[1],
        ...theme.fonts.label,
    },
    psm: {
        color: theme.colors.blue[2],
        width: 14,
        height: 14,
    },
    dates: {
        display: 'flex',
        gap: 10,
        color: theme.colors.neutral[1],
        ...theme.fonts.label,
        whiteSpace: 'nowrap',
        '&.past > span:last-child': {
            color: theme.colors.red[1],
        },
    },
});

const ColumnEquipment = (props: { asset: AssetOverview }) => {
    const classes = useTableColumnsClasses();
    return (
        <div className={classes.equipment}>
            <span>{props.asset.name}</span>
            <span>{props.asset.code}</span>
        </div>
    );
};

const ColumnType = (props: { asset: AssetOverview }) => {
    const classes = useTableColumnsClasses();
    return <span className={classes.type}>{props.asset.type ?? ''}</span>;
};

const ColumnPsm = (props: { asset: AssetOverview }) => {
    const classes = useTableColumnsClasses();
    return props.asset.psm ? <CheckCircle className={classes.psm} /> : <></>;
};

const ColumnRisk = (props: { asset: AssetOverview }) => {
    const { t } = usePrefixTranslation('common');
    const risk = computeOverallRiskLikelihoodConsequence(props.asset.likelihood_risk, props.asset.consequence_risk);
    return <Badge type={enumToStatusType[risk]}>{t(`probability.${risk}`)}</Badge>;
};

const ColumnDates = (props: { asset: AssetOverview; type: 'internal' | 'external' }) => {
    const classes = useTableColumnsClasses();
    const { t } = usePrefixTranslation('asset', { asset: 'list.AssetList' });
    const last = props.asset[`last_${props.type}` as const];
    const due = props.asset[`${props.type}_due_date` as const];
    const cls = { past: +due !== 0 && +due < +startOfDay(new Date()) };

    return (
        <div className={clsx(classes.dates, cls)}>
            <span>{last ? t('table-column-inspection-date', { date: last }) : '- - - - - - - -'}</span>
            {'/'}
            <span>{+due !== 0 ? t('table-column-inspection-date', { date: due }) : '- - - - - - - -'}</span>
        </div>
    );
};
